// src/pages/FinalStory.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStory } from '../contexts/StoryContext.tsx';
import { useFinalStatusQuery, useAssembleFinalMutation } from '../hooks/useFinalStatusQuery.ts';
import { useQueryClient } from '@tanstack/react-query';

const FinalStory: React.FC = () => {
  const navigate = useNavigate();
  const { selectedStoryId, storyStatus } = useStory();
  const [shouldPoll, setShouldPoll] = useState(false); // start polling after Assemble
  const qc = useQueryClient();

  // react-query: final status (auto refetches on focus; polls only when shouldPoll)
  const { data: final, isLoading, error, refetch } =
    useFinalStatusQuery(selectedStoryId, { shouldPoll });

  // mutation for assemble action
  const assembleMutation = useAssembleFinalMutation(selectedStoryId);

  // stop polling once ready
  useEffect(() => {
    if (final?.has_final_video && final?.final_url) setShouldPoll(false);
  }, [final?.has_final_video, final?.final_url]);

  // Early: no story
  if (!selectedStoryId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Story Selected</h2>
          <p className="text-gray-600 mb-4">Please select a story to view its final video.</p>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Go to Stories
          </button>
        </div>
      </div>
    );
  }

  // Initial hydration
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading final story...</p>
        </div>
      </div>
    );
  }

  // Error (query)
  if (error instanceof Error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Final Story</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button onClick={() => refetch()} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const finalUrl = final?.final_url ?? null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {finalUrl ? (
          // --- Final video ready ---
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">Your Story is Ready!</h2>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="aspect-video bg-black w-full max-w-2xl">
                <video
                  src={finalUrl}
                  controls
                  className="w-full h-full"
                  preload="metadata"
                  onError={() => {
                    // If the pre-signed URL expired, refresh it
                    qc.invalidateQueries({ queryKey: ['final-status', selectedStoryId] });
                  }}
                />
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={async () => {
                      try {
                        if (navigator.share) {
                          await navigator.share({
                            title: 'My Story',
                            text: 'Check out my story!',
                            url: finalUrl
                          });
                        } else {
                          await navigator.clipboard.writeText(finalUrl);
                          alert('Link copied to clipboard!');
                        }
                      } catch (err: any) {
                        if (err?.name !== 'AbortError') {
                          try {
                            await navigator.clipboard.writeText(finalUrl);
                            alert('Link copied to clipboard!');
                          } catch (clipboardErr) {
                            console.error('Clipboard failed:', clipboardErr);
                          }
                        }
                      }
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Share
                  </button>
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = finalUrl;
                      link.download = `story-${selectedStoryId}.mp4`;
                      link.click();
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Download
                  </button>
                </div>
                <div className="text-sm text-gray-500">Story ID: {selectedStoryId}</div>
              </div>
            </div>
          </div>
        ) : shouldPoll ? (
          // --- Actively assembling (query is polling) ---
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-blue-600 mb-6">
              <div className="animate-spin rounded-full h-24 w-24 border-b-4 border-blue-600 mx-auto"></div>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Creating Your Final Story</h2>
            <p className="text-gray-600 mb-6">
              We're assembling your final video. This may take a few minutes...
            </p>
            <div className="flex items-center justify-center space-x-2 text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm">Processing...</span>
            </div>
          </div>
        ) : (
          // --- Not ready yet / assemble CTA ---
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-gray-400 mb-6">
              <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Final Story Not Ready</h2>
            <p className="text-gray-600 mb-6">
              Once all scenes and videos are complete, you can assemble the final story.
            </p>

            <div className="space-y-4">
              <button
                onClick={async () => {
                  try {
                    await assembleMutation.mutateAsync();
                    setShouldPoll(true); // start polling until URL appears
                  } catch (e) {
                    // surface a minimal error; you can toast here if you want
                    alert('Failed to assemble final story');
                  }
                }}
                disabled={assembleMutation.isPending}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assembleMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Starting Assembly...
                  </div>
                ) : (
                  'Assemble Final Story'
                )}
              </button>

              <div className="text-sm text-gray-500">
                <p>Make sure all characters, scenes, and videos are complete first.</p>
                <p>You can check the progress in the Characters, Scenes, and Videos pages.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalStory;
